import {
  Box,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SxProps,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import { Accordion } from "../../../main/components/accordion/Accordion";
import { ClientInfo } from "../../clients/pages/ClientDetails";
import { useContext, useEffect, useRef, useState } from "react";
import PrimaryBaseButton from "../../../main/components/PrimaryBaseButton";
import { Screen } from "../../../main/components/screen/Screen";
import CheckIcon from "@mui/icons-material/Check";
import { alarmsApi } from "../../../services/api";
import {
  AlarmDetails,
  AlarmEventInterval,
  AlarmTimeline,
  SeverityTypesEnum,
  alarmEventGroup,
} from "../types";
import { TypeInfo } from "../../../main/components/TypeInfo/TypeInfo";
import { RopChart } from "../../../main/components/RopChart";
import { EChartsOption } from "echarts";
import CardBox from "../../../main/components/CardBox";
import WarningIcon from "@mui/icons-material/Warning";
import EmptyStateNotFound from "../../clients/components/EmptyState";
import Loading from "../../../main/components/Loading";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { IoIosArrowBack } from "react-icons/io";
import { formatDateAndHour, parseTimeSpan } from "../../../main/utils/utils";
import { MainContext } from "../../../main/context/MainContextProvider";
import { useLocation, useNavigate } from "react-router-dom";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";
import CardDownload from "../../faq/components/CardDownload";

export default function AlarmsDetails() {
  const location = useLocation();
  const locationState = location.state

  const path = window.location.pathname;

  const alarmUsinaName = locationState ? locationState.usinaName : decodeURIComponent(path.split("/")[2]);
  const alarmUsinaId = locationState ? locationState.usinaId : path.split("/")[3];
  const alarmUG = locationState ? locationState.ug : path.split("/")[4];
  const alarmArea = locationState ? locationState.area : path.split("/")[5];
  const alarmCode = locationState ? locationState.code : path.split("/")[6];
  const alarmString: string | undefined = locationState ? locationState.string : undefined
  const alarmMessage: string | undefined = locationState ? locationState.message : undefined

  const { isMobile } = useContext(MainContext);

  const [showPossibleSolutionsDetails, setShowPossibleSolutionsDetails] =
    useState<boolean>(false);
  const togglePossibleSolutionsDetails = () => {
    setShowPossibleSolutionsDetails(!showPossibleSolutionsDetails);
  };

  const [showPossibleCausesDetails, setShowPossibleCausesDetails] =
    useState<boolean>(false);
  const togglePossibleCausesDetails = () => {
    setShowPossibleCausesDetails(!showPossibleCausesDetails);
  };

  const [loading, setLoading] = useState<boolean>(true);
  const [timelineLoading, setTimelineLoading] = useState<boolean>(true);
  const [selectedString, setSelectedString] = useState<string | undefined>(alarmString);
  const [alarmDetails, setAlarmDetails] = useState<AlarmDetails | null>(null);
  const [alarmTimelineResponse, setAlarmTimelineResponse] = useState<AlarmTimeline | null>(
    null
  );
  const [alarmTimeline, setAlarmTimeline] = useState<alarmEventGroup>();
  const [alarmEventInterval, setAlarmEventInterval] =
    useState<AlarmEventInterval>(AlarmEventInterval.LastDay);
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedString && alarmTimelineResponse && alarmTimelineResponse.alarmEventGroups && alarmTimelineResponse.alarmEventGroups.length > 0) {
      const currentGroup = alarmTimelineResponse?.alarmEventGroups?.find((item) => (item.stringName == "null" ? "outros" : item.stringName) == selectedString);
      setAlarmTimeline(currentGroup);
    }
  }, [selectedString, alarmTimelineResponse])

  async function updateTimeline() {
    setTimelineLoading(true);
    try {
      const alarmTimelineResponse = await alarmsApi.getAlarmTimeline(
        alarmUsinaId,
        alarmUG,
        getRequestArea(alarmArea) ?? alarmArea,
        alarmCode,
        alarmEventInterval
      );
      setAlarmTimelineResponse(alarmTimelineResponse);
      if (!(alarmTimelineResponse.alarmEventGroups.length > 0)) {
        setAlarmTimeline(undefined)
      }
      if ((alarmTimelineResponse.alarmEventGroups.length == 1 && alarmTimelineResponse.alarmEventGroups[0].stringName == "null")) {
        setSelectedString("outros")
      }
    } catch (error) {
      throw new Error("Erro ao carregar timeline do alarme");
    } finally {
      setTimelineLoading(false);
    }
  }

  async function updateDetails() {
    setLoading(true);
    try {
      const alarmDetailsResponse = await alarmsApi.getAlarmDetails(alarmCode);
      setAlarmDetails(alarmDetailsResponse);
    } catch (error) {
      throw new Error("Erro ao carregar detalhes do alarme");
    } finally {
      setLoading(false);
    }
  }

  const onMount = async () => {
    await updateDetails();
  };
  useEffect(() => {
    updateTimeline();
  }, [alarmEventInterval]);

  useEffect(() => {
    onMount();
    updateTimeline();
    setAlarmEventInterval(AlarmEventInterval.LastDay);
  }, [alarmCode, alarmArea, alarmUG, alarmUsinaId, alarmString])

  useEffect(() => {
    setSelectedString(alarmString)
  }, [alarmString])

  const elementRef = useRef(null);

  const isActiveChart = alarmTimeline?.alarmTimeLineList
    .map((e) => (e.active ? "Ativo" : "Inativo"));

  const eventChartValues = alarmTimeline?.alarmTimeLineList
    .map((_e) => ("Evento"));

  const createdAtChart = alarmTimeline?.alarmTimeLineList
    .map((e) => formatDateAndHour(e.startDateTime));

  const activeTrueEvents = alarmTimeline?.alarmTimeLineList.filter(
    (event) => event.active === true
  );
  const activeFalseEvents = alarmTimeline?.alarmTimeLineList.filter(
    (event) => event.active === false
  );

  const lastActiveTrueEvent = activeTrueEvents && activeTrueEvents[activeTrueEvents.length - 1];

  const lastActiveFalseEvent = activeFalseEvents && activeFalseEvents[activeFalseEvents.length - 1];

  const createdAtLastActiveTrue = lastActiveTrueEvent
    ? formatDateAndHour(lastActiveTrueEvent.startDateTime)
    : "Não informado";
  const createdAtLastActiveFalse = lastActiveFalseEvent
    ? formatDateAndHour(lastActiveFalseEvent.startDateTime)
    : "Não informado";

  const sliderIsEnabled = alarmEventInterval == AlarmEventInterval.LastMonth || (eventChartValues && eventChartValues.length > 60);

  const evtOption: EChartsOption = {
    title: {
      text: "Linha do tempo do alarme",
      textStyle: {
        fontSize: 14,
        fontWeight: "bold",
      },
    },
    grid: { containLabel: true, top: sliderIsEnabled ? 30 : 40, bottom: sliderIsEnabled ? 50 : 0 },
    legend: {
      top: 0,
      left: "left",
    },

    dataZoom: sliderIsEnabled ?
      {
        type: 'slider',
        show: sliderIsEnabled,
        xAxisIndex: 0,
        orient: 'horizontal',
        top: sliderIsEnabled ? 127 : 17,
        start: 0,
        end: 10,
      } : undefined,
    tooltip: {},
    axisLabel: {
      formatter: function (value: string) {
        if (value.length >= 18) {
          var line2 = value.slice(0, 5);
          var line1 = value.slice(12, 18);
          return line1 + "\n" + line2;
        } else {
          return value;
        }
      },
    },
    backgroundColor: "transparent",
    xAxis: {
      type: "category",
      data: createdAtChart,
    },
    yAxis: {
      type: "category",
      data: ["Evento"],
    },
    series: [
      {
        data: eventChartValues,
        type: "line",
        step: "end",
        symbol: "circle",
        symbolSize: 4,
        lineStyle: {
          color: "transparent",
        }
      },
    ],
  };

  const almOption: EChartsOption = {
    title: {
      text: "Linha do tempo do alarme",
      textStyle: {
        fontSize: 14,
        fontWeight: "bold",
      },
    },
    grid: { containLabel: true, top: sliderIsEnabled ? 30 : 40, bottom: sliderIsEnabled ? 50 : 0 },
    legend: {
      top: 0,
      left: "left",
    },
    dataZoom: sliderIsEnabled ?
      {
        type: 'slider',
        show: sliderIsEnabled,
        xAxisIndex: 0,
        orient: 'horizontal',
        top: sliderIsEnabled ? 127 : 17,
        start: 0,
        end: 10,
      } : undefined,
    tooltip: {},
    axisLabel: {
      formatter: function (value: string) {
        if (value.length >= 18) {
          var line2 = value.slice(0, 5);
          var line1 = value.slice(12, 18);
          return line1 + "\n" + line2;
        } else {
          return value;
        }
      },
    },
    backgroundColor: "transparent",
    xAxis: {
      type: "category",
      data: createdAtChart,
    },
    yAxis: {
      type: "category",
      data: ["Inativo", "Ativo"],
    },
    series: [
      {
        data: isActiveChart,
        type: "line",
        step: "end",
      },
    ],
  };

  const tooltipSSButton = () => {
    if (!alarmTimelineResponse?.alarmIsActive)
      return "Apenas alarmes ativos podem abrir SS";

    if (alarmTimelineResponse?.currentIdSS) {
      return "Este alarme já possui SS";
    }

    return "";
  };

  const disabledSSButton = () => {
    if (!alarmTimelineResponse?.alarmIsActive) return true;

    if (alarmTimelineResponse?.currentIdSS) {
      return true;
    }

    return false;
  };

  const handleClickInspectionProcedure = () => {
    if (alarmDetails?.actions?.hasInspectionProcedure) {
      const downloadButton = document.createElement("a");
      downloadButton.href = alarmDetails.actions.inspectionProcedure ?? "";
      downloadButton.download = alarmDetails.actions.inspectionProcedure ?? "";
      downloadButton.click();
    }
  };

  function OrderArray(array: string[]): string[] {
    return array.sort((a, b) => {
      const numeroA = parseInt(a.split(" ").pop() || "0");
      const numeroB = parseInt(b.split(" ").pop() || "0");
      return numeroA - numeroB;
    });
  }

  const stringArrayOrdenado = OrderArray(alarmTimelineResponse?.alarmEventGroups?.map((alarm) => alarm.stringName) ?? []);

  function getRequestArea(valor: string): string | null {
    const mapa: Record<string, string> = {
      TELECOM: "COM",
      INSTRUMENTAÇÃO: "INST",
      MONTANTE: "MNT",
      REFRIGERAÇÃO: "REG",
      SUBESTAÇÃO: "SUB",
      TRAFO: "TRF",
      VEDAÇÃO: "VED",
      INVERSOR: "INV",
      TRACKER: "TRK",
    };

    return mapa[valor.toUpperCase()] ?? null;
  }


  return (
    <Screen.Root>
      {loading ? (
        <>
          <Screen.Container>
            <Loading />
          </Screen.Container>
        </>
      ) : alarmDetails ? (
        <>
          <Screen.Header sx={{ alignItems: "flex-start" }}>
            <Grid
              item
              xs={1}
              sm={0.5}
              sx={{ marginLeft: "-20px", marginRight: "10px" }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <IconButton
                  onClick={() => {
                    navigate("/alarm/equipment-alarms-list");
                  }}
                  sx={{
                    width: {
                      xs: "35px",
                      sm: "40px",
                    },
                    height: {
                      xs: "35px",
                      sm: "40px",
                    },
                  }}
                >
                  <IoIosArrowBack />
                </IconButton>
              </Box>
            </Grid>
            <Grid
              item
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "flex-start" : "center",
              }}
              xs={10}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  marginRight: { xs: "12px", sm: "24px" },
                }}
              >
                <Typography
                  fontSize={{
                    xs: "13px",
                    sm: "17px",
                  }}
                >
                  {alarmDetails?.name ?? "Não informado"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                  }}
                >
                  {`${alarmDetails.code ? alarmDetails.code?.toUpperCase() : ""}${alarmUsinaName ? " - " + alarmUsinaName?.toUpperCase() : ""}${alarmUG ? " - " + alarmUG?.toUpperCase() : ""}${alarmArea ? " - " + alarmArea?.toUpperCase() : ""}${alarmString ? " - " + alarmString?.toUpperCase() : ""}${alarmMessage ? " - " + alarmMessage?.toUpperCase() : ""}`}
                </Typography>
              </Box>
              {alarmDetails.type !== "EVENTO" && (
                <TypeInfo.Root
                  backgroundColor={
                    timelineLoading
                      ? "#A8B6BC"
                      : alarmTimelineResponse?.alarmIsActive
                        ? "#FF0000"
                        : "#27AE60"
                  }
                >
                  <TypeInfo.Icon
                    icon={
                      timelineLoading ? (
                        <RestartAltIcon
                          sx={{ fontSize: { sm: "18px", xs: "14px" } }}
                        />
                      ) : alarmTimelineResponse?.alarmIsActive ? (
                        <WarningIcon
                          sx={{ fontSize: { sm: "18px", xs: "14px" } }}
                        />
                      ) : (
                        <CheckIcon
                          sx={{ fontSize: { sm: "18px", xs: "14px" } }}
                        />
                      )
                    }
                  />
                  <TypeInfo.Text
                    sx={{ fontSize: { xs: "10px", sm: "13px" } }}
                    text={
                      timelineLoading
                        ? "Carregando..."
                        : alarmTimelineResponse?.alarmIsActive
                          ? "Alarme Ativo"
                          : "Alarme Inativo"
                    }
                  />
                </TypeInfo.Root>
              )}
            </Grid>
            <Grid
              sx={{ display: "flex", justifyContent: "flex-end" }}
              item
              xs={2}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Typography
                  fontSize={{
                    xs: "13px",
                    sm: "17px",
                  }}
                >
                  Severidade
                </Typography>
                {getSeverityByType(alarmDetails?.severity ?? 1)}
              </Box>
            </Grid>
          </Screen.Header>
          <Screen.Container>
            {alarmDetails.type !== "EVENTO" && (
              <Grid container sx={{ rowGap: "20px" }}>
                <Grid item xs={12} display={"flex"}>
                  <Typography
                    fontSize={{
                      xs: "13px",
                      sm: "17px",
                    }}
                    sx={{ paddingTop: "16px" }}
                  >
                    Possíveis ações
                  </Typography>
                </Grid>
                <Grid
                  display={"flex"}
                  flexDirection={"row"}
                  justifyContent={"left"}
                  rowGap={1}
                  container
                >
                  <Grid xs={12} sm={4} item>
                    <Tooltip title="Em desenvolvimento" placement="bottom">
                      <Box>
                        <PrimaryBaseButton
                          sx={{
                            width: "97%",
                            height: { xs: "50px", sm: "70px" },
                          }}
                          title="Inativar Alarme"
                        />
                      </Box>
                    </Tooltip>
                  </Grid>
                  <Grid xs={12} sm={4} item>
                    <Tooltip title={tooltipSSButton()}>
                      <Box>
                        <PrimaryBaseButton
                          disabled={disabledSSButton()}
                          onClick={() => {
                            navigate("/service-request/cadastro", {
                              state: {
                                alarmUsinaId: alarmUsinaId,
                                alarmCode: alarmCode,
                                alarmUg: alarmUG,
                                alarmArea: alarmArea,
                                alarmString: alarmString,
                                alarmUsinaName: alarmUsinaName,
                                alarmDescricao: `${alarmCode}${alarmMessage ? " - " + alarmMessage : ""}`,
                                alarmSolutions: alarmDetails?.solutions ?? "",
                                alarmLastActiveDate:
                                  alarmTimelineResponse?.alarmLastActiveDate ?? "",
                                alarmMessage: alarmMessage,
                                navigateFrom: "alarm-details",
                              },
                            });
                          }}
                          sx={{
                            width: "97%",
                            height: { xs: "50px", sm: "70px" },
                          }}
                          title="Abrir solicitação de serviço"
                        />
                      </Box>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Grid>
            )}
            {alarmTimelineResponse?.alarmEventGroups && alarmTimelineResponse?.alarmEventGroups?.length > 1 &&
              <Box sx={{
                width: "100%",
                marginTop: "15px",
                flexDirection: "row",
                display: "flex",
                Height: "100%"
              }}>
                <Typography
                  fontSize={{
                    xs: "13px",
                    sm: "17px",
                  }}
                  marginRight={3}
                >
                  Selecionar String
                </Typography>
                <FormControl sx={{ width: "150px" }} size="small">
                  <InputLabel id="demo-simple-select-label">String</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={selectedString}
                    label="Age"
                    sx={{ height: '30px' }}
                    onChange={(e) => setSelectedString(e.target.value)}
                  >
                    {stringArrayOrdenado.map((stringName) => {
                      if (stringName == "null")
                        return <MenuItem value={stringName}>outros</MenuItem>
                      else
                        return <MenuItem value={stringName}>{stringName}</MenuItem>
                    }
                    )}
                  </Select>
                </FormControl>
              </Box>
            }
            <CardBox
              sx={{
                width: "100%",
                marginTop: "15px",
                marginBottom: "15px",
                flexDirection: "row",
              }}
            >
              {timelineLoading ? (
                <Loading />
              ) : (
                <Grid sx={{ minHeight: "300px" }} container>
                  <>
                    <Grid
                      item
                      sm={6}
                      xs={12}
                      sx={{
                        display: "flex",
                        flexDirection: "column-reverse",
                        justifyContent: "center",
                      }}
                    >
                      {alarmTimeline &&
                        alarmTimeline.alarmTimeLineList.length > 0 ? (
                        <>
                          {alarmTimeline.activeTime && alarmTimeline.currentAlarmEvent.groupType !== "EVT" && (
                            <Box
                              sx={{
                                display: "flex",
                                width: "100%",
                                justifyContent: "center",
                                marginBottom: "16px",
                              }}
                            >
                              <ClientInfo
                                removePaddingInMobile
                                paddingLeft
                                justifyContent="center"
                                title="Tempo ativo"
                                value={parseTimeSpan(
                                  alarmTimeline.activeTime
                                )}
                              />
                            </Box>
                          )}
                          <Container
                            ref={elementRef}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              height: { xs: "140px", sm: "200px" },
                              width: { xs: "280px", sm: "550px" },
                              justifyContent: "center",
                              paddingTop: "15px",
                              paddingBottom: "15px",
                            }}
                          >
                            <RopChart option={alarmTimeline.currentAlarmEvent.groupType == "EVT" ? evtOption : almOption} />
                          </Container>

                          {alarmTimeline.currentAlarmEvent.groupType !== "EVT" &&
                            <Box
                              sx={{
                                flexDirection: "row",
                                display: "flex",
                                width: "100%",
                                justifyContent: "space-evenly",
                              }}
                            >
                              <ClientInfo
                                removePaddingInMobile
                                paddingLeft
                                justifyContent="center"
                                title="Ultima ativação"
                                value={createdAtLastActiveTrue}
                              />
                              <ClientInfo
                                removePaddingInMobile
                                paddingLeft
                                justifyContent="center"
                                title="Ultima Inativação"
                                value={createdAtLastActiveFalse}
                              />
                            </Box>}
                        </>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          <Typography>Nenhum Registro Encontrado</Typography>
                        </Box>
                      )}
                    </Grid>
                    <Grid
                      item
                      sm={6}
                      xs={12}
                      sx={{
                        padding: { sm: "15px", xs: "15px" },
                        flexDirection: "column",
                        display: "flex",
                        backgroundColor: "rgba(217, 217, 217, 0.1)",
                        borderRadius: 2,
                        rowGap: "16px ",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",

                          flexDirection: { sm: "row", xs: "column" },
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: isMobile ? "center" : undefined,
                            paddingBottom: isMobile ? "15px" : undefined,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: { xs: "13px", sm: "17px" },
                            }}
                          >
                            Histórico de Ocorrências
                          </Typography>
                          <Typography
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              fontSize: "11px",
                              color: "#FF9F0C",
                            }}
                          >
                            {alarmTimeline &&
                              alarmTimeline.alarmTimeLineList.length > 1
                              ? "Eventos"
                              : "Evento"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            columnGap: 1,
                            alignItems: "center",
                            justifyContent: isMobile ? "center" : undefined,
                          }}
                        >
                          <PrimaryBaseButton
                            isActive={
                              alarmEventInterval === AlarmEventInterval.LastDay
                            }
                            onClick={() =>
                              setAlarmEventInterval(AlarmEventInterval.LastDay)
                            }
                            sx={{ height: "25px" }}
                            title="Dia"
                          />
                          <PrimaryBaseButton
                            isActive={
                              alarmEventInterval === AlarmEventInterval.LastWeek
                            }
                            onClick={() =>
                              setAlarmEventInterval(AlarmEventInterval.LastWeek)
                            }
                            sx={{ height: "25px" }}
                            title="Semana"
                          />
                          <PrimaryBaseButton
                            isActive={
                              alarmEventInterval ===
                              AlarmEventInterval.LastMonth
                            }
                            onClick={() =>
                              setAlarmEventInterval(
                                AlarmEventInterval.LastMonth
                              )
                            }
                            sx={{ height: "25px" }}
                            title="Mês"
                          />
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                          height: "100%",
                          maxHeight: "300px",
                          overflow: "auto",
                        }}
                      >
                        {alarmTimeline &&
                          alarmTimeline.alarmTimeLineList.length > 0 ? (
                          alarmTimeline.alarmTimeLineList.map((e, i) => (
                            <Box
                              key={i}
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                                paddingRight: "10px",
                                paddingLeft: "10px",
                                width: "100%",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: "0.8rem",
                                  }}
                                >
                                  Horario da Ocorrência
                                </Typography>

                                <Typography
                                  sx={{
                                    height: "100%",
                                    alignItems: "center",
                                    display: "flex",
                                    fontSize: "0.9rem",
                                  }}
                                >
                                  {formatDateAndHour(e.startDateTime) ??
                                    "Não informado"}
                                </Typography>
                              </Box>

                              {alarmDetails.type !== "EVENTO" && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <Typography
                                    fontSize={{
                                      xs: "11px",
                                      sm: "12px",
                                    }}
                                    sx={{
                                      marginBottom: "6px",
                                    }}
                                  >
                                    Situação
                                  </Typography>
                                  <Box
                                    sx={{
                                      height: "100%",
                                      alignItems: "center",
                                      display: "flex",
                                      flexDirection: "row",
                                    }}
                                  >
                                    {e.active ? (
                                      <WarningIcon
                                        sx={{
                                          fontSize: {
                                            sm: "18px",
                                            xs: "14px",
                                          },
                                          marginRight: "8px",
                                          color: "#FF0000",
                                        }}
                                      />
                                    ) : (
                                      <CheckIcon
                                        sx={{
                                          fontSize: {
                                            sm: "18px",
                                            xs: "14px",
                                          },
                                          marginRight: "8px",
                                          color: "#27AE60",
                                        }}
                                      />
                                    )}
                                    <Typography
                                      fontSize={{
                                        xs: "11px",
                                        sm: "12px",
                                      }}
                                      color={e.active ? "#FF0000" : "#27AE60"}
                                    >
                                      {e.active ? "Ativo" : "inativo"}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexDirection: "column",
                                }}
                              >
                                <Typography
                                  fontSize={{
                                    xs: "11px",
                                    sm: "12px",
                                  }}
                                  sx={{
                                    marginBottom: "3px",
                                  }}
                                >
                                  SS
                                </Typography>
                                <Tooltip
                                  title={
                                    e.idSS
                                      ? "Ir para SS"
                                      : "esse alarme não possui SS"
                                  }
                                >
                                  <IconButton
                                    disabled={e.idSS ? false : true}
                                    sx={{
                                      width: {
                                        xs: "30px",
                                        sm: "32px",
                                      },
                                      height: {
                                        xs: "30px",
                                        sm: "32px",
                                      },
                                      marginLeft: {
                                        xs: "auto",
                                        sm: "0px",
                                      },
                                      backgroundColor: true
                                        ? "#FF9F0C"
                                        : "#444142",
                                      borderRadius: "8px",
                                    }}
                                    onClick={() =>
                                      navigate(`/service-request/${e.idSS}`)
                                    }
                                    type={"button"}
                                  >
                                    <ElectricalServicesIcon
                                      sx={{
                                        color: e.idSS ? "#FFF" : "#c6c6c6",
                                      }}
                                    />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          )).reverse()
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              width: "100%",
                              height: "100%",
                            }}
                          >
                            <Typography>Nenhum Registro Encontrado</Typography>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  </>
                </Grid>
              )}
            </CardBox>
            <Grid
              sx={{
                padding: { sm: "15px", xs: "15px" },
                flexDirection: "row",
                display: "flex",
                backgroundColor: "rgba(217, 217, 217, 0.1)",
                borderRadius: 2,
                marginTop: "8px",
                marginBottom: "8px",
                rowGap: "16px ",
              }}
              container
            >
              <Grid sx={{ display: "flex" }} item xs={12}>
                <Typography>Detalhes</Typography>
              </Grid>
              <ClientInfo
                removePaddingInMobile
                paddingLeft
                title="Nome"
                value={alarmDetails?.name ?? "Não informado"}
              />
              <ClientInfo
                removePaddingInMobile
                paddingLeft
                title="Técnico Especializado"
                value={
                  alarmDetails?.actions?.specializedTechnical ?? "Não informado"
                }
              />
              <ClientInfo
                removePaddingInMobile
                paddingLeft
                title="Assistência Técnica"
                value={
                  alarmDetails?.actions?.technicalAssistance ?? "Não informado"
                }
              />
              <ClientInfo
                removePaddingInMobile
                paddingLeft
                title="Código do Alarme"
                value={alarmDetails?.code ?? "Não informado"}
              />
              <ClientInfo
                removePaddingInMobile
                paddingLeft
                title="Provedor"
                value={alarmDetails?.actions?.provider ?? "Não informado"}
              />
              <ClientInfo
                removePaddingInMobile
                paddingLeft
                title="Concessionária"
                value={alarmDetails?.actions?.dealership ?? "Não informado"}
              />
            </Grid>
            <Accordion.Root>
              <Accordion.Header
                toggleDetails={togglePossibleCausesDetails}
                showDetails={showPossibleCausesDetails}
                title="Possíveis causas"
                typographySx={{ fontSize: { xs: "13px", sm: "17px" } }}
              />
              <Accordion.Content showDetails={showPossibleCausesDetails}>
                {alarmDetails?.causes ?? "Não informado"}
              </Accordion.Content>
            </Accordion.Root>
            <Accordion.Root>
              <Accordion.Header
                toggleDetails={togglePossibleSolutionsDetails}
                showDetails={showPossibleSolutionsDetails}
                title="Possíveis soluções"
                typographySx={{ fontSize: { xs: "13px", sm: "17px" } }}
              />
              <Accordion.Content showDetails={showPossibleSolutionsDetails}>
                {alarmDetails?.solutions ?? "Não informado"}
              </Accordion.Content>
            </Accordion.Root>
            <Box sx={{ width: "100%", display: "flex", marginTop: "16px" }}>
              <Typography
                sx={{
                  fontSize: { xs: "13px", sm: "16px" },
                  color: "#FFF",
                  marginRight: "8px",
                }}
              >
                Procedimentos de inspeção
              </Typography>
            </Box>
            <Grid sx={{ paddingY: "15px" }}>
              <CardDownload
                sx={{
                  cursor: alarmDetails?.actions?.hasInspectionProcedure
                    ? "pointer"
                    : "default",
                  padding: "15px",
                }}
                label={
                  alarmDetails?.actions?.hasInspectionProcedure ? (
                    <>
                      Clique para baixar o documento <br />{" "}
                      {alarmDetails.actions.inspectionProcedureName}
                    </>
                  ) : (
                    "Nenhum documento de procedimento de inspeção encontrado."
                  )
                }
                onClick={handleClickInspectionProcedure}
                tooltipText={
                  alarmDetails?.actions?.hasInspectionProcedure
                    ? ""
                    : "Nenhum arquivo encontrado"
                }
              />
            </Grid>
          </Screen.Container>
        </>
      ) : (
        <Screen.Container>
          <EmptyStateNotFound />
        </Screen.Container>
      )}
    </Screen.Root>
  );
}
export function getSeverityByType(
  type: SeverityTypesEnum,
  sxText?: SxProps<Theme>
) {
  switch (type) {
    case SeverityTypesEnum.bland:
      return (
        <Typography
          sx={{
            fontSize: "13px",
            ...sxText,
          }}
        >
          Brando
        </Typography>
      );
    case SeverityTypesEnum.medium:
      return (
        <Typography
          sx={{
            fontSize: "13px",
            color: "#FF9F0C",
            ...sxText,
          }}
        >
          Médio
        </Typography>
      );
    case SeverityTypesEnum.severe:
      return (
        <Typography
          sx={{
            fontSize: "13px",
            color: "#FF0000",
            ...sxText,
          }}
        >
          Severo
        </Typography>
      );
    default:
      return (
        <Typography
          sx={{
            fontSize: "13px",
            ...sxText,
          }}
        >
          Severidade não informada
        </Typography>
      );
  }
}
